import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  IconButton,
  Divider,
  Tooltip,
  Button,
  CircularProgress,
  Snackbar,
  Alert
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  Editor, 
  EditorState, 
  RichUtils, 
  convertToRaw, 
  convertFromRaw, 
  CompositeDecorator,
  Modifier
} from 'draft-js';
import 'draft-js/dist/Draft.css';
import agthealthService from '../../../../services/agthealthService';
import { useTasksContext } from '../../../../contexts/TasksContext'; // Importar el contexto de tareas

// Importación de iconos
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';

// Color dorado
const goldColor = '#E3AE08';

// Mapa de estilos personalizados
const styleMap = {
  'STRIKETHROUGH': {
    textDecoration: 'line-through',
  }
};

// Función para manejar los estilos de bloque personalizados
const blockStyleFn = (contentBlock) => {
  const type = contentBlock.getType();
  switch (type) {
    case 'center-align':
      return 'align-center';
    case 'right-align':
      return 'align-right';
    case 'left-align':
      return 'align-left';
    default:
      return '';
  }
};

// Componente de checkbox
const CheckboxComponent = (props) => {
  const { contentState, entityKey } = props;
  const { checked } = contentState.getEntity(entityKey).getData();
  
  return (
    <span contentEditable={false} style={{ marginRight: '5px' }}>
      <input 
        type="checkbox" 
        checked={checked} 
        onChange={() => {
          const newData = { checked: !checked };
          props.contentState.mergeEntityData(entityKey, newData);
        }} 
      />
    </span>
  );
};

// Función para encontrar entidades de checkbox
const findCheckboxEntities = (contentBlock, callback, contentState) => {
  contentBlock.findEntityRanges(
    (character) => {
      const entityKey = character.getEntity();
      return (
        entityKey !== null &&
        contentState.getEntity(entityKey).getType() === 'CHECKBOX'
      );
    },
    callback
  );
};

// Estilos
const NotesPaper = styled(Paper)(({ theme }) => ({
  backgroundColor: '#ffffff',
  borderRadius: '12px',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  border: 'none',
  position: 'relative', // Para posicionar el botón AGT Task
  width: '100%', // Asegura que el componente tome el ancho de su contenedor
  maxWidth: '100%', // Evita que se desborde de su contenedor
}));

const NotesHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(1.5),
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(10px)',
  position: 'sticky',
  top: 0,
  zIndex: 1,
}));

const FormattingToolbar = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0.5, 1),
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: '#f9f9f9',
  overflowX: 'auto',
  '&::-webkit-scrollbar': {
    height: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#E3AE08',
    borderRadius: '3px',
  }
}));

const NotesContent = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  padding: theme.spacing(2, 3),
  overflow: 'auto',
  width: '100%', // Asegura que tome el ancho completo
  maxWidth: '100%', // Limita el ancho máximo
  boxSizing: 'border-box', // Incluye padding en el cálculo del ancho
  '&::-webkit-scrollbar': {
    width: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#E3AE08',
    borderRadius: '3px',
  }
}));

const EditorWrapper = styled(Box)(({ theme }) => ({
  height: '100%',
  width: '100%', // Asegura que tome el ancho completo
  '& .DraftEditor-root': {
    height: '100%',
    width: '100%', // Ancho completo
  },
  '& .DraftEditor-editorContainer': {
    height: '100%',
    width: '100%', // Ancho completo
  },
  '& .public-DraftEditor-content': {
    minHeight: '100%',
    padding: '4px',
    fontSize: '1rem',
    lineHeight: '1.5',
    color: '#333333',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    maxHeight: '400px', // Altura máxima para activar el scroll vertical
    overflowY: 'auto', // Scroll vertical
    overflowX: 'hidden', // Oculta el scroll horizontal
    width: '100%', // Ancho completo
    wordWrap: 'break-word', // Rompe palabras largas
    whiteSpace: 'pre-wrap', // Mantiene espacios pero permite saltos de línea
  },
  // Estilos para las alineaciones
  '& .align-center': {
    textAlign: 'center',
  },
  '& .align-right': {
    textAlign: 'right',
  },
  '& .align-left': {
    textAlign: 'left',
  },
  // Estilos para el scrollbar slim
  '& .public-DraftEditor-content::-webkit-scrollbar': {
    width: '4px',
  },
  '& .public-DraftEditor-content::-webkit-scrollbar-thumb': {
    backgroundColor: '#E3AE08',
    borderRadius: '3px',
  },
  '& .public-DraftEditor-content::-webkit-scrollbar-track': {
    background: 'transparent',
  }
}));

// Componente para el botón AGT Task
const AGTTaskButton = styled(Button)(({ theme }) => ({
  position: 'absolute',
  bottom: '10px',
  right: '10px',
  backgroundColor: goldColor,
  color: '#FFFFFF',
  fontWeight: 'bold',
  padding: '4px 10px',
  minWidth: 'unset',
  borderRadius: '15px',
  fontSize: '0.75rem',
  textTransform: 'none',
  boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
  '&:hover': {
    backgroundColor: '#c49b07',
  },
  '&:disabled': {
    backgroundColor: '#d0d0d0',
    color: '#888888',
  },
  zIndex: 10,
  opacity: 0.9,
}));

const QuickNotesWidget = () => {
  // Acceder al contexto de tareas
  const { refreshTasks } = useTasksContext();
  
  // Crear decorador para checkboxes
  const createDecorator = () => {
    return new CompositeDecorator([
      {
        strategy: findCheckboxEntities,
        component: CheckboxComponent,
      },
    ]);
  };

  // Estado para el editor
  const [editorState, setEditorState] = useState(() => 
    EditorState.createEmpty(createDecorator())
  );
  const [lastEdited, setLastEdited] = useState(null);
  
  // Estado para el ID de la nota
  const [noteId, setNoteId] = useState(null);
  
  // Estados para el procesamiento de AGT Task
  const [isProcessing, setIsProcessing] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  // Cargar contenido guardado al iniciar
  useEffect(() => {
    const loadNotes = async () => {
      try {
        const response = await agthealthService.getUserNotes();
        
        if (response.success && response.data.length > 0) {
          // Ordenar por fecha de actualización y obtener la más reciente
          const notes = response.data.sort((a, b) => 
            b.updatedAt - a.updatedAt
          );
          
          const latestNote = notes[0];
          setNoteId(latestNote.noteId);
          
          try {
            const contentState = convertFromRaw(JSON.parse(latestNote.content));
            setEditorState(EditorState.createWithContent(contentState, createDecorator()));
            setLastEdited(new Date(latestNote.updatedAt));
          } catch (e) {
            console.error('Error al cargar la nota guardada:', e);
          }
        }
      } catch (error) {
        console.error('Error al cargar notas:', error);
        
        // Fallback a localStorage si hay error
        const savedContent = localStorage.getItem('quickNoteContent');
        const savedTimestamp = localStorage.getItem('quickNoteTimestamp');
        
        if (savedContent) {
          try {
            const contentState = convertFromRaw(JSON.parse(savedContent));
            setEditorState(EditorState.createWithContent(contentState, createDecorator()));
          } catch (e) {
            console.error('Error al cargar la nota guardada del localStorage:', e);
          }
        }
        
        if (savedTimestamp) {
          setLastEdited(new Date(parseInt(savedTimestamp)));
        }
      }
    };
    
    loadNotes();
  }, []);
  
  // Guardar contenido cuando cambia (con debounce)
  const [saveTimeout, setSaveTimeout] = useState(null);
  
  const handleEditorChange = (newEditorState) => {
    setEditorState(newEditorState);
    const now = new Date();
    setLastEdited(now);
    
    // Guardar en localStorage como respaldo
    const contentState = newEditorState.getCurrentContent();
    const contentRaw = JSON.stringify(convertToRaw(contentState));
    localStorage.setItem('quickNoteContent', contentRaw);
    localStorage.setItem('quickNoteTimestamp', now.getTime().toString());
    
    // Debounce para no hacer muchas llamadas al API
    if (saveTimeout) clearTimeout(saveTimeout);
    
    const newTimeout = setTimeout(async () => {
      try {
        // Solo guardar si hay contenido
        if (!contentState.hasText()) return;
        
        const noteData = {
          noteId, // será null en la primera vez
          content: contentRaw,
          title: 'Nota rápida', // Título por defecto
        };
        
        const response = await agthealthService.saveNote(noteData);
        
        if (response.success && response.data) {
          setNoteId(response.data.noteId);
        }
      } catch (error) {
        console.error('Error al guardar la nota en el servidor:', error);
      }
    }, 1000); // esperar 1 segundo de inactividad antes de guardar
    
    setSaveTimeout(newTimeout);
  };

  // Formato de fecha
  const formatDate = (date) => {
    if (!date) return '';
    
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const noteDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    if (noteDate.getTime() === today.getTime()) {
      return `Hoy ${date.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}`;
    } 
    
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (noteDate.getTime() === yesterday.getTime()) {
      return `Ayer ${date.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}`;
    }
    
    return date.toLocaleDateString([], {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Aplicar formato de texto
  const handleFormatText = (style, type = 'inline') => {
    if (type === 'inline') {
      setEditorState(RichUtils.toggleInlineStyle(editorState, style));
    } else if (type === 'block') {
      if (style === 'checkbox') {
        // Implementación de checkbox
        const contentState = editorState.getCurrentContent();
        const contentStateWithEntity = contentState.createEntity(
          'CHECKBOX',
          'IMMUTABLE',
          { checked: false }
        );
        const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
        const selectionState = editorState.getSelection();
        
        // Insertar la entidad en el texto
        const textWithEntity = Modifier.insertText(
          contentStateWithEntity,
          selectionState,
          ' ',
          null,
          entityKey
        );
        
        // Crear nuevo estado del editor con la entidad
        const newEditorState = EditorState.push(
          editorState,
          textWithEntity,
          'insert-characters'
        );
        
        setEditorState(newEditorState);
      } else {
        setEditorState(RichUtils.toggleBlockType(editorState, style));
      }
    }
  };

  // Verificar si un estilo está activo
  const isStyleActive = (style, type = 'inline') => {
    if (type === 'inline') {
      return editorState.getCurrentInlineStyle().has(style);
    } else if (type === 'block') {
      const selection = editorState.getSelection();
      const blockType = editorState
        .getCurrentContent()
        .getBlockForKey(selection.getStartKey())
        .getType();
      return blockType === style;
    }
    return false;
  };

  // Manejador para cerrar notificaciones
  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  // Manejador para el botón AGT Task
  const handleAGTTaskClick = async () => {
    if (!noteId) {
      setNotification({
        open: true,
        message: 'No hay nota guardada para crear tarea',
        severity: 'error'
      });
      return;
    }
    
    setIsProcessing(true);
    
    try {
      const response = await agthealthService.createTaskFromNote(noteId);
      
      if (response.success) {
        setNotification({
          open: true,
          message: 'Tarea creada correctamente con AGT Task',
          severity: 'success'
        });
        
        // Usar el contexto para actualizar el widget de tareas
        refreshTasks();
        
        // También emitir un evento y actualizar localStorage como respaldo
        const taskRefreshEvent = new CustomEvent('agtTaskCreated', { 
          detail: { taskId: response.data?.taskId || null } 
        });
        window.dispatchEvent(taskRefreshEvent);
        localStorage.setItem('lastTaskCreatedAt', Date.now().toString());
      }
    } catch (error) {
      setNotification({
        open: true,
        message: error.message || 'Error al crear tarea desde nota',
        severity: 'error'
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <NotesPaper elevation={0}>
      <NotesHeader>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" sx={{ 
            fontWeight: '700',
            fontSize: '1.1rem',
            color: '#000000',
            letterSpacing: '-0.01em',
          }}>
            Notas
          </Typography>
          {lastEdited && (
            <Typography variant="caption" sx={{ 
              color: '#757575',
              fontSize: '0.75rem',
              display: 'block',
              fontWeight: '500'
            }}>
              {formatDate(lastEdited)}
            </Typography>
          )}
        </Box>
      </NotesHeader>
      
      <FormattingToolbar>
        <Tooltip title="Negrita">
          <IconButton 
            size="small" 
            onClick={() => handleFormatText('BOLD')}
            sx={{ 
              color: isStyleActive('BOLD') ? '#000000' : goldColor,
              backgroundColor: isStyleActive('BOLD') ? 'rgba(227, 174, 8, 0.2)' : 'transparent'
            }}
          >
            <FormatBoldIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        
        <Tooltip title="Cursiva">
          <IconButton 
            size="small" 
            onClick={() => handleFormatText('ITALIC')}
            sx={{ 
              color: isStyleActive('ITALIC') ? '#000000' : goldColor,
              backgroundColor: isStyleActive('ITALIC') ? 'rgba(227, 174, 8, 0.2)' : 'transparent'
            }}
          >
            <FormatItalicIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        
        <Tooltip title="Subrayado">
          <IconButton 
            size="small" 
            onClick={() => handleFormatText('UNDERLINE')}
            sx={{ 
              color: isStyleActive('UNDERLINE') ? '#000000' : goldColor,
              backgroundColor: isStyleActive('UNDERLINE') ? 'rgba(227, 174, 8, 0.2)' : 'transparent'
            }}
          >
            <FormatUnderlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        
        <Tooltip title="Tachado">
          <IconButton 
            size="small" 
            onClick={() => handleFormatText('STRIKETHROUGH')}
            sx={{ 
              color: isStyleActive('STRIKETHROUGH') ? '#000000' : goldColor,
              backgroundColor: isStyleActive('STRIKETHROUGH') ? 'rgba(227, 174, 8, 0.2)' : 'transparent'
            }}
          >
            <StrikethroughSIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        
        <Tooltip title="Lista con viñetas">
          <IconButton 
            size="small" 
            onClick={() => handleFormatText('unordered-list-item', 'block')}
            sx={{ 
              color: isStyleActive('unordered-list-item', 'block') ? '#000000' : goldColor,
              backgroundColor: isStyleActive('unordered-list-item', 'block') ? 'rgba(227, 174, 8, 0.2)' : 'transparent'
            }}
          >
            <FormatListBulletedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        
        <Tooltip title="Lista numerada">
          <IconButton 
            size="small" 
            onClick={() => handleFormatText('ordered-list-item', 'block')}
            sx={{ 
              color: isStyleActive('ordered-list-item', 'block') ? '#000000' : goldColor,
              backgroundColor: isStyleActive('ordered-list-item', 'block') ? 'rgba(227, 174, 8, 0.2)' : 'transparent'
            }}
          >
            <FormatListNumberedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        
        <Tooltip title="Casilla de verificación">
          <IconButton 
            size="small" 
            onClick={() => handleFormatText('checkbox', 'block')}
            sx={{ 
              color: isStyleActive('checkbox', 'block') ? '#000000' : goldColor,
              backgroundColor: isStyleActive('checkbox', 'block') ? 'rgba(227, 174, 8, 0.2)' : 'transparent'
            }}
          >
            <CheckBoxOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        
        <Tooltip title="Alinear a la izquierda">
          <IconButton 
            size="small" 
            onClick={() => handleFormatText('left-align', 'block')}
            sx={{ 
              color: isStyleActive('left-align', 'block') ? '#000000' : goldColor,
              backgroundColor: isStyleActive('left-align', 'block') ? 'rgba(227, 174, 8, 0.2)' : 'transparent'
            }}
          >
            <FormatAlignLeftIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        
        <Tooltip title="Centrar">
          <IconButton 
            size="small" 
            onClick={() => handleFormatText('center-align', 'block')}
            sx={{ 
              color: isStyleActive('center-align', 'block') ? '#000000' : goldColor,
              backgroundColor: isStyleActive('center-align', 'block') ? 'rgba(227, 174, 8, 0.2)' : 'transparent'
            }}
          >
            <FormatAlignCenterIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        
        <Tooltip title="Alinear a la derecha">
          <IconButton 
            size="small" 
            onClick={() => handleFormatText('right-align', 'block')}
            sx={{ 
              color: isStyleActive('right-align', 'block') ? '#000000' : goldColor,
              backgroundColor: isStyleActive('right-align', 'block') ? 'rgba(227, 174, 8, 0.2)' : 'transparent'
            }}
          >
            <FormatAlignRightIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </FormattingToolbar>
      
      <NotesContent>
        <EditorWrapper>
          <Editor
            editorState={editorState}
            onChange={handleEditorChange}
            placeholder="Escribe aquí..."
            customStyleMap={styleMap}
            blockStyleFn={blockStyleFn}
          />
        </EditorWrapper>
      </NotesContent>
      
      {/* Botón AGT Task con tooltip */}
      <Tooltip 
        title="AGT creará tareas en base a tus notas" 
        placement="top"
        arrow
      >
        <AGTTaskButton 
          onClick={handleAGTTaskClick}
          disabled={isProcessing}
        >
          {isProcessing ? (
            <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
          ) : null}
          Usar AGT Task
        </AGTTaskButton>
      </Tooltip>
      
      {/* Notificaciones */}
      <Snackbar 
        open={notification.open} 
        autoHideDuration={6000} 
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </NotesPaper>
  );
};

export default QuickNotesWidget;